import { API_BASE_URL, EXPECTED_ROLE } from "../config";
import { clearSession, readSession, writeSession } from "./session-store";
import type {
  Category,
  Dish,
  DishDraft,
  DeviceRegistration,
  Order,
  OrderList,
  OrderStatus,
  TokenResponse,
  User,
} from "./types";

type RequestOptions = RequestInit & { retryAuth?: boolean };

export class ApiError extends Error {
  constructor(message: string, readonly status: number, readonly detail?: unknown) {
    super(message);
  }
}

function errorMessage(payload: unknown, fallback: string): string {
  if (payload && typeof payload === "object" && "detail" in payload) {
    const detail = (payload as { detail: unknown }).detail;
    if (typeof detail === "string") return detail;
  }
  return fallback;
}

class ApiClient {
  private session: TokenResponse | null = null;

  async hydrate(): Promise<TokenResponse | null> {
    this.session = await readSession();
    return this.session;
  }

  get currentSession(): TokenResponse | null {
    return this.session;
  }

  private async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const headers = new Headers(options.headers);
    if (this.session?.access_token) headers.set("Authorization", `Bearer ${this.session.access_token}`);
    if (options.body && !(options.body instanceof FormData) && !headers.has("Content-Type")) {
      headers.set("Content-Type", "application/json");
    }
    const response = await fetch(`${API_BASE_URL}${path}`, { ...options, headers });
    if (response.status === 401 && options.retryAuth !== false && this.session?.refresh_token) {
      const refreshed = await this.refresh();
      if (refreshed) return this.request<T>(path, { ...options, retryAuth: false });
    }
    if (!response.ok) {
      const payload = await response.json().catch(() => null);
      throw new ApiError(errorMessage(payload, `请求失败（${response.status}）`), response.status, payload);
    }
    if (response.status === 204) return undefined as T;
    return response.json() as Promise<T>;
  }

  async login(email: string, password: string): Promise<TokenResponse> {
    const session = await this.request<TokenResponse>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password, device_id: `${EXPECTED_ROLE}-mobile` }),
      retryAuth: false,
    });
    if (session.user.role !== EXPECTED_ROLE) {
      this.session = session;
      await this.request<void>("/auth/logout", { method: "POST", retryAuth: false }).catch(() => undefined);
      this.session = null;
      throw new ApiError(
        EXPECTED_ROLE === "customer" ? "这个账号不是点菜账号" : "这个账号没有管理权限",
        403,
      );
    }
    this.session = session;
    await writeSession(session);
    return session;
  }

  async refresh(): Promise<boolean> {
    if (!this.session?.refresh_token) return false;
    const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: this.session.refresh_token }),
    });
    if (!response.ok) {
      this.session = null;
      await clearSession();
      return false;
    }
    const session = await response.json() as TokenResponse;
    if (session.user.role !== EXPECTED_ROLE) {
      this.session = null;
      await clearSession();
      return false;
    }
    this.session = session;
    await writeSession(session);
    return true;
  }

  async restore(): Promise<User | null> {
    await this.hydrate();
    if (!this.session) return null;
    try {
      const user = await this.request<User>("/auth/me");
      if (user.role !== EXPECTED_ROLE) throw new Error("role mismatch");
      return user;
    } catch {
      this.session = null;
      await clearSession();
      return null;
    }
  }

  async logout(): Promise<void> {
    if (this.session) await this.request<void>("/auth/logout", { method: "POST" }).catch(() => undefined);
    this.session = null;
    await clearSession();
  }

  listCategories(): Promise<Category[]> {
    return this.request("/dish-categories");
  }

  listDishes(admin = false): Promise<Dish[]> {
    return this.request(admin ? "/admin/dishes" : "/dishes");
  }

  listOrders(): Promise<OrderList> {
    return this.request("/orders?limit=100");
  }

  createOrder(payload: { mealTime: string; note: string; items: Array<{ dishId: string; quantity: number }> }, idempotencyKey: string): Promise<Order> {
    return this.request("/orders", {
      method: "POST",
      headers: { "Idempotency-Key": idempotencyKey },
      body: JSON.stringify({
        meal_time: payload.mealTime,
        note: payload.note,
        items: payload.items.map((item) => ({ dish_id: item.dishId, quantity: item.quantity })),
      }),
    });
  }

  transitionOrder(order: Order, status: OrderStatus, reason?: string): Promise<Order> {
    const paths: Partial<Record<OrderStatus, string>> = {
      accepted: "accept",
      declined: "decline",
      cooking: "start-cooking",
      ready: "ready",
      completed: "complete",
    };
    const action = paths[status];
    if (!action) throw new Error("不支持的订单状态");
    return this.request(`/orders/${order.id}/${action}`, {
      method: "POST",
      body: JSON.stringify({ version: order.version, reason: reason || null }),
    });
  }

  async uploadDishImage(file: File, width?: number, height?: number): Promise<string> {
    const upload = await this.request<{ image_id: string; upload_url: string }>("/media/uploads", {
      method: "POST",
      body: JSON.stringify({ file_name: file.name, content_type: file.type, size_bytes: file.size }),
    });
    const isApiUpload = upload.upload_url.startsWith("/");
    const uploadUrl = isApiUpload ? `${API_BASE_URL}${upload.upload_url.replace(/^\/api\/v1/, "")}` : upload.upload_url;
    const headers = new Headers({ "Content-Type": file.type });
    if (isApiUpload && this.session?.access_token) {
      headers.set("Authorization", `Bearer ${this.session.access_token}`);
    }
    const put = await fetch(uploadUrl, { method: "PUT", headers, body: file });
    if (!put.ok) throw new ApiError("图片上传失败", put.status);
    await this.request(`/media/uploads/${upload.image_id}/complete`, {
      method: "POST",
      body: JSON.stringify({ width: width || null, height: height || null }),
    });
    return upload.image_id;
  }

  async createDish(draft: DishDraft, imageSize?: { width: number; height: number }): Promise<Dish> {
    if (!draft.image) throw new Error("新菜需要上传图片");
    const imageId = await this.uploadDishImage(draft.image, imageSize?.width, imageSize?.height);
    return this.request("/dishes", {
      method: "POST",
      body: JSON.stringify({
        name: draft.name,
        category_id: draft.categoryId,
        estimated_minutes: draft.estimatedMinutes,
        image_id: imageId,
      }),
    });
  }

  async updateDish(dish: Dish, draft: DishDraft, imageSize?: { width: number; height: number }): Promise<Dish> {
    const imageId = draft.image
      ? await this.uploadDishImage(draft.image, imageSize?.width, imageSize?.height)
      : undefined;
    return this.request(`/dishes/${dish.id}`, {
      method: "PATCH",
      body: JSON.stringify({
        name: draft.name,
        category_id: draft.categoryId,
        estimated_minutes: draft.estimatedMinutes,
        version: dish.version,
        ...(imageId ? { image_id: imageId } : {}),
      }),
    });
  }

  setDishAvailability(dish: Dish, available: boolean): Promise<Dish> {
    return this.request(`/dishes/${dish.id}/availability`, {
      method: "PUT",
      body: JSON.stringify({ available, version: dish.version }),
    });
  }

  deleteDish(dish: Dish): Promise<void> {
    return this.request(`/dishes/${dish.id}`, { method: "DELETE" });
  }

  registerDevice(
    platform: "ios" | "android" | "web",
    pushToken: string,
    keys?: { p256dh: string; auth: string },
  ): Promise<DeviceRegistration> {
    return this.request("/devices", {
      method: "POST",
      body: JSON.stringify({
        platform,
        push_token: pushToken,
        ...(keys || {}),
      }),
    });
  }

  disableDevice(deviceId: string): Promise<void> {
    return this.request(`/devices/${deviceId}`, { method: "DELETE" });
  }
}

export const api = new ApiClient();
